import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function NavChevron({ direction }) {
  return (
    <svg className="clr-icon" width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d={direction === 'prev' ? 'M10 3L5 8L10 13' : 'M6 3L11 8L6 13'}
        stroke="#1C2B2A"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

NavChevron.propTypes = {
  direction: PropTypes.oneOf(['prev', 'next']).isRequired,
};

function buildMonthDays(monthDate) {
  const start = startOfWeek(startOfMonth(monthDate));
  const end = endOfWeek(endOfMonth(monthDate));
  return eachDayOfInterval({ start, end });
}

function MonthGrid({
  monthDate,
  startDate,
  endDate,
  hoverDate,
  onSelectDay,
  onHoverDay,
}) {
  const days = useMemo(() => buildMonthDays(monthDate), [monthDate]);
  const previewEnd = endDate || hoverDate;

  const getDayClass = (day) => {
    const classes = ['clr-range-picker__day'];
    if (!isSameMonth(day, monthDate)) classes.push('is-outside');

    const hasStart = Boolean(startDate);
    const hasEnd = Boolean(endDate);
    const inPreview = hasStart && previewEnd;

    if (hasStart && isSameDay(day, startDate)) classes.push('is-start');
    if ((hasEnd && isSameDay(day, endDate)) || (!hasEnd && hoverDate && startDate && isSameDay(day, hoverDate))) {
      classes.push('is-end');
    }
    if (hasStart && hasEnd && isSameDay(startDate, endDate) && isSameDay(day, startDate)) {
      classes.push('is-single');
    }

    if (inPreview) {
      const rangeStart = isBefore(startDate, previewEnd) ? startDate : previewEnd;
      const rangeEnd = isAfter(startDate, previewEnd) ? startDate : previewEnd;
      const afterStart = isAfter(day, rangeStart) || isSameDay(day, rangeStart);
      const beforeEnd = isBefore(day, rangeEnd) || isSameDay(day, rangeEnd);
      if (afterStart && beforeEnd) classes.push('is-in-range');
    }

    return classes.join(' ');
  };

  return (
    <div className="clr-range-picker__month">
      <div className="clr-range-picker__month-label">{format(monthDate, 'MMM yyyy')}</div>
      <div className="clr-range-picker__weekdays">
        {WEEKDAYS.map((day) => (
          <span key={`${format(monthDate, 'yyyy-MM')}-${day}`}>{day}</span>
        ))}
      </div>
      <div className="clr-range-picker__grid">
        {days.map((day) => (
          <button
            key={day.toISOString()}
            type="button"
            className={getDayClass(day)}
            onClick={() => onSelectDay(startOfDay(day))}
            onMouseEnter={() => onHoverDay(startOfDay(day))}
            onMouseLeave={() => onHoverDay(null)}
          >
            <span>{format(day, 'd')}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

MonthGrid.propTypes = {
  monthDate: PropTypes.instanceOf(Date).isRequired,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  hoverDate: PropTypes.instanceOf(Date),
  onSelectDay: PropTypes.func.isRequired,
  onHoverDay: PropTypes.func.isRequired,
};

function parseDateInput(value) {
  if (!value) return null;
  if (value instanceof Date) return startOfDay(value);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return startOfDay(parsed);
}

function ClrDateRangePicker({
  open,
  startDate,
  endDate,
  onApply,
  onReset,
  onClose,
  panelRef,
  panelStyle,
}) {
  const initialStart = parseDateInput(startDate);
  const initialEnd = parseDateInput(endDate);
  const [leftMonth, setLeftMonth] = useState(() => startOfMonth(initialStart || new Date()));
  const [draftStart, setDraftStart] = useState(initialStart);
  const [draftEnd, setDraftEnd] = useState(initialEnd);
  const [hoverDate, setHoverDate] = useState(null);

  useEffect(() => {
    if (!open) return;
    const nextStart = parseDateInput(startDate);
    const nextEnd = parseDateInput(endDate);
    setDraftStart(nextStart);
    setDraftEnd(nextEnd);
    setHoverDate(null);
    setLeftMonth(startOfMonth(nextStart || new Date()));
  }, [open, startDate, endDate]);

  const rightMonth = addMonths(leftMonth, 1);
  const canApply = Boolean(draftStart && draftEnd);
  const canReset = Boolean(draftStart || draftEnd || startDate || endDate);

  const handleSelectDay = (day) => {
    if (!draftStart || (draftStart && draftEnd)) {
      setDraftStart(day);
      setDraftEnd(null);
      setHoverDate(null);
      return;
    }

    if (isBefore(day, draftStart)) {
      setDraftStart(day);
      setDraftEnd(draftStart);
      return;
    }

    setDraftEnd(day);
  };

  const handlePrev = () => setLeftMonth((prev) => subMonths(prev, 1));
  const handleNext = () => setLeftMonth((prev) => addMonths(prev, 1));

  const handleReset = () => {
    setDraftStart(null);
    setDraftEnd(null);
    setHoverDate(null);
    onReset?.();
  };

  const handleApply = () => {
    if (!draftStart || !draftEnd) return;
    onApply?.(draftStart, draftEnd);
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className="clr-range-picker"
      ref={panelRef}
      style={panelStyle || undefined}
      role="dialog"
      aria-label="Select date range"
    >
      <div className="clr-range-picker__header">
        <button type="button" className="clr-range-picker__nav" aria-label="Previous month" onClick={handlePrev}>
          <NavChevron direction="prev" />
        </button>
        <h3 className="clr-range-picker__title">Select date range</h3>
        <button type="button" className="clr-range-picker__nav" aria-label="Next month" onClick={handleNext}>
          <NavChevron direction="next" />
        </button>
      </div>

      <div className="clr-range-picker__months">
        <MonthGrid
          monthDate={leftMonth}
          startDate={draftStart}
          endDate={draftEnd}
          hoverDate={hoverDate}
          onSelectDay={handleSelectDay}
          onHoverDay={setHoverDate}
        />
        <MonthGrid
          monthDate={rightMonth}
          startDate={draftStart}
          endDate={draftEnd}
          hoverDate={hoverDate}
          onSelectDay={handleSelectDay}
          onHoverDay={setHoverDate}
        />
      </div>

      <div className="clr-range-picker__footer">
        <button
          type="button"
          className={`clr-range-picker__reset${canReset ? ' is-active' : ''}`}
          onClick={handleReset}
          disabled={!canReset}
        >
          Reset
        </button>
        <button
          type="button"
          className={`clr-range-picker__apply${canApply ? ' is-active' : ''}`}
          onClick={handleApply}
          disabled={!canApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

ClrDateRangePicker.propTypes = {
  open: PropTypes.bool,
  startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  onApply: PropTypes.func,
  onReset: PropTypes.func,
  onClose: PropTypes.func,
  panelRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  panelStyle: PropTypes.object,
};

export default ClrDateRangePicker;
